import type { KnowledgeArticle, Locale } from "../types"
import { aiAgentLatenBouwen } from "./ai-agent-laten-bouwen"
import { ragChatbotLatenBouwen } from "./rag-chatbot-laten-bouwen"
import { aiKennisbankVoorBedrijven } from "./ai-kennisbank-voor-bedrijven"
import { aiAgentsVsAutomatisering } from "./ai-agents-vs-automatisering"
import { ragVsFineTuning } from "./rag-vs-fine-tuning"
import { watKostMaatwerkAi } from "./wat-kost-maatwerk-ai"
import { vanAiPocNaarProductie } from "./van-ai-poc-naar-productie"
import { aiIntegrerenBedrijfssoftware } from "./ai-integreren-bedrijfssoftware"
import { aiAgentBedrijfsdata } from "./ai-agent-bedrijfsdata"
import { wanneerGeenAi } from "./wanneer-geen-ai"
import { watIsEenAiAgent } from "./wat-is-een-ai-agent"
import { aiAutomatiseringMkb } from "./ai-automatisering-mkb"

/**
 * Knowledge articles.
 *
 * Dutch is the primary language for these technical articles. English versions
 * are only added when substantive translated content exists — never as
 * machine-translated filler to inflate page counts. Articles without a slug in
 * a locale simply have no URL in that locale (and therefore no hreflang pair).
 */
export const knowledgeArticles: KnowledgeArticle[] = [
  aiAgentLatenBouwen,
  ragChatbotLatenBouwen,
  aiKennisbankVoorBedrijven,
  aiAgentsVsAutomatisering,
  ragVsFineTuning,
  watKostMaatwerkAi,
  vanAiPocNaarProductie,
  aiIntegrerenBedrijfssoftware,
  aiAgentBedrijfsdata,
  wanneerGeenAi,
  watIsEenAiAgent,
  aiAutomatiseringMkb,
].sort((a, b) => b.published.localeCompare(a.published))

export function getArticle(id: string): KnowledgeArticle | undefined {
  return knowledgeArticles.find((a) => a.id === id)
}

export function getArticleBySlug(slug: string, locale: Locale): KnowledgeArticle | undefined {
  return knowledgeArticles.find((a) => a.slug[locale] === slug)
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

/**
 * Realistic reading time derived from the article's actual word count
 * (~220 words per minute), with a minimum of one minute. Computed instead of
 * hand-maintained so it can never drift from the real content length.
 */
export function getReadingTimeMinutes(article: KnowledgeArticle): number {
  let words = 0
  for (const section of article.sections) {
    switch (section.type) {
      case "paragraph":
      case "callout":
      case "heading":
        words += countWords(section.text)
        break
      case "list":
        for (const item of section.items) words += countWords(item)
        break
    }
  }
  return Math.max(1, Math.round(words / 220))
}

export function getArticlesForLocale(locale: Locale): KnowledgeArticle[] {
  return knowledgeArticles.filter((a) => Boolean(a.slug[locale]))
}

export const knowledgeIndexContent = {
  nl: {
    title: "Kennis",
    intro:
      "Technische uitleg uit onze praktijk over het bouwen van AI-agents, RAG-kennisbanken en maatwerk AI-systemen. Geen marketingverhalen — wat werkt, wat kost en wanneer je juist niet moet starten.",
    emptyState: "Er zijn nog geen artikelen beschikbaar.",
    availableInNotice: "Dit artikel is alleen in het Nederlands beschikbaar.",
  },
  en: {
    title: "Insights",
    intro:
      "Technical explainers from our practice on building AI agents, RAG knowledge systems and custom AI solutions. No marketing fluff — what works, what it costs, and when you shouldn't start at all.",
    emptyState: "No articles are available yet.",
    availableInNotice: "This article is currently only available in Dutch.",
  },
} as const
